import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";
import { Typography, Grid } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import {
  RESIDENT_STATUS,
  STATUS,
} from "../../../config/constant";
import TextFieldInput from "../../../components/TextFieldInput";
import BoxCheckInput from "../../../components/BoxCheckInput";
import UploadInput from "../../../components/UploadInput";
import CommonButtonVoucherForm from "../../../shared-components/common-button/CommonButtonVoucherForm";
import {
  setApplicantActiveStep,
  setApplicantActiveStep1,
  setApplicantActiveStep2,
  setEditPreviewApplicant,
  setShowUploadAadharImage,
  setShowUploadOCIAlternateImage,
  setShowUploadOCIImage,
  setShowUploadPanImage,
  setShowUploadPassportImage,
  setStepsCompleted,
  updateApplicantDetailsForm,
} from "../voucher-form.slice";
import { route } from "../../../services/apiRoutes";
import UploadPopup from "../../../shared-components/upload-popup/UploadPopup";
import UploadPopupTwoImages from "../../../shared-components/upload-popup/UploadPopupTwoImages";
import { removeBlobImages } from "../../../utilis/common-funtions";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import GoogleMapsAutocomplete from "../../../shared-components/google-maps-autocomplete/GoogleMapsAutocomplete";
import { useVoucherFormMode } from "../../../utilis/use-voucher-form-mode";

const ContactDetails = forwardRef(({ applicantNo }, ref) => {
  const dispatch = useDispatch();
  const { voucherId } = useParams();
  const history = useHistory();
  const { isApplicantUpdateMode } = useVoucherFormMode();
  // In the "Update Applicant Info" flow, the primary applicant's identity
  // documents (PAN, Aadhaar, Passport for NRI/OCI) must remain locked.
  const isPrimaryLockedField = isApplicantUpdateMode && applicantNo === 1;

  const {
    showUploadAadharImage,
    showUploadPassportImage,
    showUploadOCIImage,
    showUploadOCIAlternateImage,
    showUploadPanImage,
    applicantData,
    applicantActiveStep,
    activeStep,
    stepsCompleted
  } = useSelector((state) => state.voucherForm);

  const residentStatus =
    applicantData?.[`applicant${applicantNo}`]?.personalDetails
      ?.residentStatus || "";
  const contactDetailFormik = useFormik({
    initialValues: {
      panNumber: "",
      panImage: [],
      aadhaarNumber: "",
      aadhaarImage: [],
      isSameAddress: null,
      OCIAlternateDocType: "",
      OCIAlternateDocImage: [],
      passportNumber: "",
      passportImage: [],
      ociImage: [],
      ociNumber: "",
      isPhysicalPan: false,
      isPhysicalAadhaar: false,
      isPhysicalPassport: false,
      isPhysicalOCI: false,
      isPhysicalOtherDoc: false,
      isPassportCurrentAddress: 0,
      communicationAddress: {
        areaName: "",
        houseNumber: "",
        city: "",
        state: "",
        country: "",
        pinCode: "",
      },
      permanentAddress: {
        areaName: "",
        houseNumber: "",
        city: "",
        state: "",
        country: "",
        pinCode: "",
      },
    },
    validationSchema: yup.object().shape({
      aadhaarNumber: yup.string().when("isPhysicalAadhaar", {
        is: (isPhysicalAadhaar) =>
          isPhysicalAadhaar === false &&
          residentStatus === RESIDENT_STATUS?.Indian,
        then: () =>
          yup
            .string()
            .matches(
              /^[2-9]\d{3}\d{4}\d{4}$/,
              "Aadhaar Number must be a 12-digit number starting from 2-9")
            .required("Aadhaar Number is required"),
        otherwise: () => yup.string().notRequired(),
      }),

      panNumber: yup
        .string()
        .notRequired()
        .when("isPhysicalPan", {
          is: true,
          then: () => yup.string().notRequired(),
          otherwise: () =>
            yup
              .string()
              .matches(/^[A-Za-z]{5}\d{4}[A-Za-z]$/, "PAN Number must be in the format ABCDE1234F")
              .required("PAN Number is required"),
        }),

      passportNumber: yup
        .string()
        .when("isPhysicalPassport",
          {
            is: (isPhysicalPassport) => isPhysicalPassport === false &&
              (residentStatus === RESIDENT_STATUS?.Nri || residentStatus === RESIDENT_STATUS?.Oci),
            then: () => yup.string().required("Passport Number is required"),
            otherwise: () => yup.string().notRequired(),
          }),

      ociNumber: yup.string().when("isPhysicalOCI",
        {
          is: (isPhysicalOCI) => isPhysicalOCI === false && residentStatus === RESIDENT_STATUS?.Oci,
          then: () => yup.string().required("OCI Number is required"),
          otherwise: () => yup.string().notRequired(),
        }),

      OCIAlternateDocType: yup
        .string()
        .notRequired()
        .when("isPassportCurrentAddress", (ll, schema) => {
          return !contactDetailFormik?.values?.isPassportCurrentAddress &&
            (residentStatus === RESIDENT_STATUS?.Oci ||
              residentStatus === RESIDENT_STATUS?.Nri)
            ? yup.string().required("Other Document is required")
            : yup.string().notRequired();
        }),

      communicationAddress: yup.object().shape({
        areaName: yup
          .string()
          .notRequired()
          .when("applicantNo", (ll, schema) => {
            return applicantNo === 2
              ? yup.string().notRequired()
              : yup.string().required("Area Name is required");
          }),
        houseNumber: yup
          .string()
          .notRequired()
          .when("applicantNo", (ll, schema) => {
            return applicantNo === 2
              ? yup.string().notRequired()
              : yup.string().required("House Number is required");
          }),
        city: yup
          .string()
          .notRequired()
          .when("applicantNo", (ll, schema) => {
            return applicantNo === 2
              ? yup.string().notRequired()
              : yup.string().required("City is required");
          }),
        state: yup
          .string()
          .notRequired()
          .when("applicantNo", (ll, schema) => {
            return applicantNo === 2
              ? yup.string().notRequired()
              : contactDetailFormik?.values?.communicationAddress?.country ===
                "" ||
                contactDetailFormik?.values?.communicationAddress?.country ===
                "India"
                ? yup.string().required("State is required")
                : yup.string().notRequired();
          }),
        country: yup
          .string()
          .notRequired()
          .when("applicantNo", (ll, schema) => {
            return applicantNo === 2
              ? yup.string().notRequired()
              : yup.string().required("Country is required");
          }),
        pinCode: yup
          .string()
          .notRequired()
          .when("applicantNo", (ll, schema) => {
            return applicantNo === 2
              ? yup.string().notRequired()
              : contactDetailFormik?.values?.communicationAddress?.country ===
                "" ||
                contactDetailFormik?.values?.communicationAddress?.country ===
                "India"
                ? yup.number().nullable().required("Pincode is required")
                : yup.string().notRequired();
            // return yup.string().notRequired()
          }),
      }),
      permanentAddress: yup.object().shape({
        areaName: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            contactDetailFormik?.values?.isSameAddress === 0,
          then: () => yup.string().required("Area Name is required"),
          otherwise: () => yup.string().notRequired(),
        }),
        houseNumber: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            contactDetailFormik?.values?.isSameAddress === 0,
          then: () => yup.string().required("House Number is required"),
          otherwise: () => yup.string().notRequired(),
        }),
        city: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            contactDetailFormik?.values?.isSameAddress === 0,
          then: () => yup.string().required("City is required"),
          otherwise: () => yup.string().notRequired(),
        }),
        state: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            contactDetailFormik?.values?.isSameAddress === 0,
          then: () =>
            contactDetailFormik?.values?.permanentAddress?.country === "" ||
              contactDetailFormik?.values?.permanentAddress?.country === "India"
              ? yup.string().required("State is required")
              : yup.string().notRequired(),
          otherwise: () => yup.string().notRequired(),
        }),
        country: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            contactDetailFormik?.values?.isSameAddress === 0,
          then: () => yup.string().required("Country is required"),
          otherwise: () => yup.string().notRequired(),
        }),
        pinCode: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            contactDetailFormik?.values?.isSameAddress === 0,
          then: () =>
            contactDetailFormik?.values?.permanentAddress?.country === "" ||
              contactDetailFormik?.values?.permanentAddress?.country === "India"
              ? yup.number().nullable().required("Pin Code is required")
              : yup.string().notRequired(),
          otherwise: () => yup.string().notRequired(),
        }),
      }),

      isSameAddress: yup.number().required("Permanent Address is required"),
      panImage: yup.array().when("isPhysicalPan", {
        is: false,
        then: () =>
          yup
            .array()
            .min(1, "Please upload PAN Card")
            .required("PAN Card is required"),
        otherwise: () => yup.array().notRequired(),
      }),
      aadhaarImage: yup.array().when("isPhysicalAadhaar", {
        is: (isPhysicalAadhaar) =>
          isPhysicalAadhaar === false &&
          residentStatus === RESIDENT_STATUS?.Indian,
        then: () =>
          yup
            .array()
            .min(1, "Please upload Aadhaar Card")
            .required("Aadhar Card is required"),
        otherwise: () => yup.array().notRequired(),
      }),

      passportImage: yup.array().when("isPhysicalPassport", {
        is: (isPhysicalPassport) =>
          isPhysicalPassport === false &&
          (residentStatus === RESIDENT_STATUS?.Nri ||
            residentStatus === RESIDENT_STATUS?.Oci),
        then: () =>
          yup
            .array()
            .min(1, "Please upload Passport Card")
            .required("Passport Card is required"),
        otherwise: () => yup.array().notRequired(),
      }),
      ociImage: yup.array().when("isPhysicalOCI", {
        is: (isPhysicalOCI) =>
          isPhysicalOCI === false && residentStatus === RESIDENT_STATUS?.Oci,
        then: () =>
          yup
            .array()
            .min(1, "Please upload OCI Card")
            .required("OCI Card is required"),
        otherwise: () => yup.array().notRequired(),
      }),
      OCIAlternateDocImage: yup.array().when("isPhysicalOtherDoc", {
        is: (isPhysicalOtherDoc) =>
          isPhysicalOtherDoc === false &&
          contactDetailFormik?.values?.isPassportCurrentAddress === 0 &&
          (residentStatus === RESIDENT_STATUS?.Oci ||
            residentStatus === RESIDENT_STATUS?.Nri),
        then: () =>
          yup
            .array()
            .min(1, "Please upload Other Document")
            .required("Other Document is required"),
        otherwise: () => yup.array().notRequired(),
      }),
    }),
  });
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  useEffect(() => {
    dispatch(setShowUploadAadharImage(false));
    dispatch(setShowUploadPanImage(false));
    dispatch(setShowUploadPassportImage(false));
    dispatch(setShowUploadOCIImage(false));
    dispatch(setShowUploadOCIAlternateImage(false));
  }, [dispatch]);
  useEffect(() => {
    if (applicantData[`applicant${applicantNo}`] !== null) {
      contactDetailFormik.setValues(
        applicantData[`applicant${applicantNo}`]?.contactDetails ||
        contactDetailFormik.initialValues
      );
    }
    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData, applicantNo]);

  useEffect(() => {
    if (applicantData[`applicant${applicantNo}`] !== null) {
      const contactDetails =
        applicantData[`applicant${applicantNo}`]?.contactDetails ||
        contactDetailFormik.initialValues;

      // Image fields to normalize
      const imageFields = [
        "panImage",
        "aadhaarImage",
        "passportImage",
        "ociImage",
        "OCIAlternateDocImage",
      ];

      const normalizedValues = {
        ...contactDetails,
        ...imageFields?.reduce((acc, field) => {
          acc[field] = contactDetails[field] && contactDetails[field]?.length > 0
            ? contactDetails[field]
            : [];
          return acc;
        }, {}),
      };

      contactDetailFormik?.setValues(normalizedValues);
    }

    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData, applicantNo]);


  useEffect(() => {
    contactDetailFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactDetailFormik?.values]);

  const handleOnChange = (e, index, key, list) => {
    contactDetailFormik.setErrors({});
    contactDetailFormik.setTouched({});
    if (
      key === "isSameAddress" &&
      list[index]?.value === 1 &&
      applicantNo === 2
    ) {
      let address =
        applicantData?.applicant1?.contactDetails?.permanentAddress || {};
      contactDetailFormik.setFieldValue("permanentAddress", address);
    }
    if (
      key === "isSameAddress" &&
      list[index]?.value === 0 &&
      applicantNo === 2
    ) {
      let address = {
        areaName: "",
        houseNumber: "",
        city: "",
        state: "",
        country: "",
        pinCode: null,
      };
      contactDetailFormik.setFieldValue("permanentAddress", address);
    }

    if (
      key === "isSameAddress" &&
      list[index]?.value === 0 &&
      applicantNo === 1
    ) {
      let address = {
        areaName: "",
        houseNumber: "",
        city: "",
        state: "",
        country: "",
        pinCode: null,
      };
      contactDetailFormik.setFieldValue("permanentAddress", address);
    }
    let selectedValue = list[index]?.value;
    contactDetailFormik.setFieldValue(key, selectedValue);
  };

  const handlePlaceChanged = (data) => {
    contactDetailFormik.setFieldValue(
      "communicationAddress.city",
      data?.city || ""
    );
    contactDetailFormik.setFieldValue(
      "communicationAddress.pinCode",
      data?.pinCode || ""
    );
    contactDetailFormik.setFieldValue(
      "communicationAddress.state",
      data?.state || ""
    );
    contactDetailFormik.setFieldValue(
      "communicationAddress.country",
      data?.country || ""
    );
  };

  const handlePlaceChangedPermanent = (data) => {
    contactDetailFormik.setFieldValue(
      "permanentAddress.city",
      data?.city || ""
    );
    contactDetailFormik.setFieldValue(
      "permanentAddress.pinCode",
      data?.pinCode || ""
    );
    contactDetailFormik.setFieldValue(
      "permanentAddress.state",
      data?.state || ""
    );
    contactDetailFormik.setFieldValue(
      "permanentAddress.country",
      data?.country || ""
    );
  };

  const handleMobileNumberAndEmail = () => {
    if (applicantData?.noOfApplicants) {
      for (let i = 1; i <= applicantData?.noOfApplicants; i++) {
        if (applicantData[`applicant${i}`]?.contactDetails === null) {
          return true;
        } else {
          if (applicantNo === i) {
            if (i === applicantData?.noOfApplicants) {
              return true;
            }
          } else {
            if (applicantData[`applicant${i}`]?.contactDetails?.aadhaarNumber &&
              applicantData[`applicant${i}`]?.contactDetails?.aadhaarNumber ===
              contactDetailFormik?.values?.aadhaarNumber &&
              residentStatus === RESIDENT_STATUS?.Indian
            ) {
              setSnackbarConfig({
                open: true,
                message:
                  "This Aadhaar Number is already associated with another applicant",
                severity: "error",
              });
              return false;
            } else if (applicantData[
              `applicant${i}`
            ]?.contactDetails?.panNumber &&
              applicantData[
                `applicant${i}`
              ]?.contactDetails?.panNumber?.toLowerCase() ===
              contactDetailFormik?.values?.panNumber?.toLowerCase()
            ) {
              setSnackbarConfig({
                open: true,
                message:
                  "This PAN Number is already associated with another applicant",
                severity: "error",
              });
              return false;
            } else {
              if (i === applicantData?.noOfApplicants) {
                return true;
              }
            }
          }
        }
      }
    }
  };

  const saveAndPreview = async (type) => {
    if (Object.keys(contactDetailFormik.errors).length === 0 &&
      handleMobileNumberAndEmail()) {
      const payload = {
        ...createContactDetailsPayload(type === "saveForLater"),
        lastStep: Math.max(applicantData?.lastStep, activeStep),
      }
      await dispatch(
        updateApplicantDetailsForm({
          path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
          payload: payload,
          opportunityId: voucherId,
        })
      )
        .unwrap()
        .then((res) => {
          if (res?.status === 200) {
            dispatch(setEditPreviewApplicant(false));
            history.push(`/customer-preview/${voucherId}`);

            let completedSteps = {
              ...stepsCompleted,
              [activeStep]: true,
            };
            dispatch(setStepsCompleted(completedSteps));
            dispatch(setApplicantActiveStep(0));

          }
        });
    }

  }
  // Expose the save function to the parent component
  useImperativeHandle(ref, () => ({
    handleSaveForm,
    errors: contactDetailFormik.errors
  }));

  // Helper function to create the contact details payload
  const createContactDetailsPayload = (saveForLaterFlag = false) => {
    return {
      applicantNumber: Number.parseInt(applicantNo),
      saveForLater: saveForLaterFlag,
      contactDetails: {
        ...applicantData?.[`applicant${applicantNo}`]?.contactDetails,
        panNumber: contactDetailFormik?.values?.panNumber?.toUpperCase(),
        panImage: contactDetailFormik?.values?.panImage,
        aadhaarNumber:
          residentStatus === RESIDENT_STATUS?.Indian || residentStatus === RESIDENT_STATUS?.Nri
            ? contactDetailFormik?.values?.aadhaarNumber
            : "",
        aadhaarImage:
          residentStatus === RESIDENT_STATUS?.Indian || residentStatus === RESIDENT_STATUS?.Nri
            ? contactDetailFormik?.values?.aadhaarImage
            : [],
        OCIAlternateDocType:
          !contactDetailFormik?.values?.isPassportCurrentAddress &&
            residentStatus !== RESIDENT_STATUS?.Indian
            ? contactDetailFormik?.values?.OCIAlternateDocType
            : "",
        OCIAlternateDocImage:
          !contactDetailFormik?.values?.isPassportCurrentAddress &&
            residentStatus !== RESIDENT_STATUS?.Indian
            ? contactDetailFormik?.values?.OCIAlternateDocImage
            : [],
        passportNumber:
          residentStatus === RESIDENT_STATUS?.Nri ||
            residentStatus === RESIDENT_STATUS?.Oci
            ? contactDetailFormik?.values?.passportNumber?.toUpperCase()
            : "",
        passportImage:
          residentStatus === RESIDENT_STATUS?.Nri ||
            residentStatus === RESIDENT_STATUS?.Oci
            ? contactDetailFormik?.values?.passportImage
            : [],
        ociImage:
          residentStatus === RESIDENT_STATUS?.Oci
            ? contactDetailFormik?.values?.ociImage
            : [],
        ociNumber:
          residentStatus === RESIDENT_STATUS?.Oci
            ? contactDetailFormik?.values?.ociNumber?.toUpperCase()
            : "",
        communicationAddress:
          applicantNo === 2
            ? undefined
            : {
              areaName:
                contactDetailFormik?.values?.communicationAddress
                  ?.areaName,
              houseNumber:
                contactDetailFormik?.values?.communicationAddress
                  ?.houseNumber,
              city: contactDetailFormik?.values?.communicationAddress
                ?.city,
              state:
                contactDetailFormik?.values?.communicationAddress?.state,
              country:
                contactDetailFormik?.values?.communicationAddress
                  ?.country,
              pinCode: Number.parseInt(
                contactDetailFormik?.values?.communicationAddress?.pinCode
              ),
            },
        isSameAddress: contactDetailFormik?.values?.isSameAddress,
        isPhysicalPan: contactDetailFormik?.values?.isPhysicalPan,
        isPhysicalAadhaar:
          residentStatus === RESIDENT_STATUS?.Indian || residentStatus === RESIDENT_STATUS?.Nri
            ? contactDetailFormik?.values?.isPhysicalAadhaar
            : false,
        isPhysicalPassport:
          residentStatus === RESIDENT_STATUS?.Nri ||
            residentStatus === RESIDENT_STATUS?.Oci
            ? contactDetailFormik?.values?.isPhysicalPassport
            : false,
        isPhysicalOCI:
          residentStatus === RESIDENT_STATUS?.Oci
            ? contactDetailFormik?.values?.isPhysicalOCI
            : false,
        isPhysicalOtherDoc:
          !contactDetailFormik?.values?.isPassportCurrentAddress &&
            residentStatus !== RESIDENT_STATUS?.Indian
            ? contactDetailFormik?.values?.isPhysicalOtherDoc
            : false,
        permanentAddress: {
          areaName:
            contactDetailFormik?.values?.permanentAddress?.areaName ||
            contactDetailFormik?.values?.communicationAddress?.areaName,
          houseNumber:
            contactDetailFormik?.values?.permanentAddress?.houseNumber ||
            contactDetailFormik?.values?.communicationAddress?.houseNumber,
          city:
            contactDetailFormik?.values?.permanentAddress?.city ||
            contactDetailFormik?.values?.communicationAddress?.city,
          state:
            contactDetailFormik?.values?.permanentAddress?.state ||
            contactDetailFormik?.values?.communicationAddress?.state,
          country:
            contactDetailFormik?.values?.permanentAddress?.country ||
            contactDetailFormik?.values?.communicationAddress?.country,
          pinCode: Number.parseInt(
            contactDetailFormik?.values?.permanentAddress?.pinCode ||
            contactDetailFormik?.values?.communicationAddress?.pinCode
          ),
        },
        isPassportCurrentAddress:
          residentStatus === RESIDENT_STATUS?.Indian
            ? 1
            : contactDetailFormik?.values?.isPassportCurrentAddress,
      },
    };
  };

  const handleSaveForm = (type) => {
    if (
      Object.keys(contactDetailFormik.errors).length === 0
      // handleMobileNumberAndEmail()
    ) {
      const payload = {
        ...createContactDetailsPayload(type === "saveForLater"),
        lastStep: Math.max(applicantData?.lastStep, activeStep),

      };

      dispatch(
        updateApplicantDetailsForm({
          path: `${route.UPDATE_VOUCHER_APPLICANT_DETAILS}/${voucherId}`,
          payload: payload,
          opportunityId: voucherId
        })
      ).unwrap()
        .then((response) => {
          // console.log(response, "response")
        })
        .catch((error) => {
          let errorMessage = "An error occurred while saving the form.";
          //Concat error messages of errors array
          if (error.errors?.message) {
            if (Array.isArray(error.errors.message)) {
              errorMessage = '';
              error.errors.message.forEach((msg) => {
                if (!errorMessage.includes(msg)) {
                  errorMessage += msg;
                }
              })
            } else if (typeof error.errors.message === "string") {
              errorMessage = error.errors.message;
            }
          }
          setSnackbarConfig({
            open: true,
            message: errorMessage,
            severity: "error",
          });
        })
    }
  };

  const handleUpdateImageData = (fieldName, imageData) => {
    contactDetailFormik.setFieldValue(fieldName, imageData);
  };

  const handlePrevious = () => {
    dispatch(setApplicantActiveStep(applicantActiveStep - 1));
  };

  const handleShowUploadAadharImage = () => {
    removeBlobImages();
    dispatch(setShowUploadAadharImage(!showUploadAadharImage));
  };
  const handleShowUploadPanImage = () => {
    removeBlobImages();
    dispatch(setShowUploadPanImage(!showUploadPanImage));
  };
  const handleShowUploadPassportImage = () => {
    removeBlobImages();
    dispatch(setShowUploadPassportImage(!showUploadPassportImage));
  };
  const handleShowUploadOCIImage = () => {
    removeBlobImages();
    dispatch(setShowUploadOCIImage(!showUploadOCIImage));
  };
  const handleShowUploadOCIAlternateImage = () => {
    removeBlobImages();
    dispatch(setShowUploadOCIAlternateImage(!showUploadOCIAlternateImage));
  };

  const handleImageValue = (keyName, imgName) => {
    removeBlobImages();
    contactDetailFormik.setFieldValue(keyName, imgName);
  };

  const handlePhysicalImage = (fieldName, value) => {
    if (fieldName === "panImage") {
      contactDetailFormik.setFieldValue("isPhysicalPan", value);
    }
    if (fieldName === "aadhaarImage") {
      contactDetailFormik.setFieldValue("isPhysicalAadhaar", value);
    }
    if (fieldName === "passportImage") {
      contactDetailFormik.setFieldValue("isPhysicalPassport", value);
    }
    if (fieldName === "ociImage") {
      contactDetailFormik.setFieldValue("isPhysicalOCI", value);
    }
    if (fieldName === "OCIAlternateDocImage") {
      contactDetailFormik.setFieldValue("isPhysicalOtherDoc", value);
    }
  };

  const handleChange = (event) => {
    if (event.target.name === "panNumber") {
      const input = event?.target?.value; //.toUpperCase()

      // Validate and update only if input length is <= 10 and matches pattern
      if (input.length <= 10 || input === "") {
        contactDetailFormik.setFieldValue("panNumber", input); // Update Formik value
      }
    } else if (event.target.name === "aadhaarNumber") {
      const input = event?.target?.value;

      // Update the field only if the input is numeric and <= 12 characters
      if (input.length <= 12 && (/^\d*$/.test(input) || input === "")) {
        contactDetailFormik.setFieldValue("aadhaarNumber", input);
      }
    } else if (event.target.name === "ociNumber") {
      const input = event.target.value; //.toUpperCase()

      // Allow input up to 12 characters and validate against OCI pattern
      if (input.length <= 12 || input === "") {
        contactDetailFormik.setFieldValue("ociNumber", input);
      }
    } else if (event.target.name === "passportNumber") {
      const input = event.target.value; //.toUpperCase()
      const validValue = input.replaceAll(/[^a-zA-Z0-9]/g, "");
      if (validValue.length <= 9 || validValue === "") {
        contactDetailFormik.setFieldValue("passportNumber", validValue);
      }
    } else if (event.target.name === "isPassportCurrentAddress") {
      contactDetailFormik.setFieldValue(
        "isPassportCurrentAddress",
        event.target.checked ? 1 : 0
      );
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  useEffect(() => {
    if (applicantNo === 1) {
      dispatch(setApplicantActiveStep1(applicantActiveStep));
    }

    if (applicantNo === 2) {
      dispatch(setApplicantActiveStep2(applicantActiveStep));
    }

    return () => { };
  }, [applicantActiveStep, applicantNo, dispatch]);

  return (
    <div>
      <form
        onSubmit={contactDetailFormik.handleSubmit}
        onReset={contactDetailFormik.handleReset}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
            <TextFieldInput
              field={{
                name: "panNumber",
                label: "PAN Number",
                required: !contactDetailFormik?.values?.isPhysicalPan,
                readonly: isPrimaryLockedField,
                placeholder: "Enter PAN Number",
                maxLength: 10,
                handleOnChange: handleChange,
              }}
              formik={contactDetailFormik}
            />
          </Grid>
          {applicantData[`applicant${applicantNo}`]?.personalDetails
            ?.residentStatus !== RESIDENT_STATUS?.Oci && (
              <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                <TextFieldInput
                  field={{
                    name: "aadhaarNumber",
                    label: "Aadhaar Number",
                    required: !(
                      contactDetailFormik?.values?.isPhysicalAadhaar ||
                      applicantData[`applicant${applicantNo}`]?.personalDetails
                        ?.residentStatus === RESIDENT_STATUS?.Nri ||
                      applicantData[`applicant${applicantNo}`]?.personalDetails
                        ?.residentStatus === RESIDENT_STATUS?.Oci
                    ),
                    readonly: isPrimaryLockedField,
                    placeholder: "Enter Aadhaar Number",
                    maxLength: 12,
                    handleOnChange: handleChange,
                  }}
                  formik={contactDetailFormik}
                />
              </Grid>
            )}
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={6}
            xl={6}
            className="widthFullUploadInput"
            style={{ alignContent: "flex-end" }}
          >
            <UploadInput
              sx={{ width: "100%" }}
              field={{
                name: "panImage",
                label: "",
                required: true,
                readonly: false,
                placeholder: contactDetailFormik?.values?.isPhysicalPan
                  ? "Upload Later"
                  : "Upload PAN Card",
                placeholderUpload: "PAN Card uploaded",
                handleOnClick: handleShowUploadPanImage,
                physicalCopyValue: contactDetailFormik?.values?.isPhysicalPan,
              }}
              formik={contactDetailFormik}
              disabled={isPrimaryLockedField}
            />
          </Grid>
          {applicantData[`applicant${applicantNo}`]?.personalDetails
            ?.residentStatus !== RESIDENT_STATUS?.Oci && (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={6}
                xl={6}
                className="widthFullUploadInput"
              >
                <UploadInput
                  field={{
                    name: "aadhaarImage",
                    label: "",
                    required:
                      (applicantData[`applicant${applicantNo}`]?.personalDetails
                        ?.residentStatus !== RESIDENT_STATUS?.Nri
                      ),
                    readonly: false,
                    placeholder: contactDetailFormik?.values?.isPhysicalAadhaar
                      ? "Upload Later"
                      : "Upload Aadhaar Card",
                    placeholderUpload: "Aadhaar card uploaded",
                    handleOnClick: handleShowUploadAadharImage,
                    physicalCopyValue:
                      contactDetailFormik?.values?.isPhysicalAadhaar,
                  }}
                  formik={contactDetailFormik}
                  twoImages={true}
                  disabled={isPrimaryLockedField}
                />
              </Grid>
            )}
          {residentStatus === RESIDENT_STATUS?.Indian ? null : (
            <>
              <Grid
                container
                item
                xs={12}
                sm={6}
                md={4}
                lg={6}
                xl={6}
                spacing={2}
              >
                <Grid item xs={12}>
                  <TextFieldInput
                    field={{
                      name: "passportNumber",
                      label: "Passport Number",
                      required:
                        !contactDetailFormik?.values?.isPhysicalPassport,
                      readonly: isPrimaryLockedField,
                      maxLength: 9,
                      placeholder: "Enter Passport Number",
                      handleOnChange: handleChange,
                      isPassportCurrentAddress:
                        contactDetailFormik?.values?.isPassportCurrentAddress,
                    }}
                    formik={contactDetailFormik}
                  />
                </Grid>
                <Grid item xs={12} className="widthFullUploadInput">
                  <UploadInput
                    field={{
                      name: "passportImage",
                      label: "",
                      required: true,
                      readonly: false,
                      placeholder: contactDetailFormik?.values
                        ?.isPhysicalPassport
                        ? "Upload Later"
                        : "Upload Passport",
                      placeholderUpload: "Passport uploaded",
                      handleOnClick: handleShowUploadPassportImage,
                      physicalCopyValue:
                        contactDetailFormik?.values?.isPhysicalPassport,
                    }}
                    twoImages={true}
                    formik={contactDetailFormik}
                    disabled={isPrimaryLockedField}
                  />
                </Grid>
              </Grid>

              {residentStatus === RESIDENT_STATUS?.Nri ? contactDetailFormik.values.isPassportCurrentAddress ? null : (
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={6}
                  xl={6}
                  spacing={2}
                >
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                    <TextFieldInput
                      field={{
                        name: "OCIAlternateDocType",
                        label: "Other Document",
                        required: true,
                        readonly: false,
                        maxLength: 50,
                        placeholder: "Other Document Type ",
                      }}
                      formik={contactDetailFormik}
                    />
                  </Grid>

                  <Grid item xs={12} className="widthFullUploadInput">
                    <UploadInput
                      field={{
                        name: "OCIAlternateDocImage",
                        label: "",
                        required: false,
                        readonly: false,
                        placeholder: contactDetailFormik?.values
                          ?.isPhysicalOtherDoc
                          ? "Upload Later"
                          : "Upload Other Document ",
                        placeholderUpload: "Document uploaded",
                        handleOnClick: handleShowUploadOCIAlternateImage,
                        physicalCopyValue:
                          contactDetailFormik?.values?.isPhysicalOtherDoc,
                      }}
                      formik={contactDetailFormik}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid
                  container
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={6}
                  xl={6}
                  spacing={2}
                >
                  <Grid item xs={12}>
                    <TextFieldInput
                      field={{
                        name: "ociNumber",
                        label: "OCI Number",
                        required: !(
                          contactDetailFormik?.values?.isPhysicalOCI ||
                          applicantData[`applicant${applicantNo}`]
                            ?.personalDetails?.residentStatus ===
                          RESIDENT_STATUS?.Nri
                        ),
                        readonly: false,
                        maxLength: 10,
                        placeholder: "Enter OCI Number",
                        handleOnChange: handleChange,
                      }}
                      formik={contactDetailFormik}
                    />
                  </Grid>
                  <Grid item xs={12} className="widthFullUploadInput">
                    <UploadInput
                      field={{
                        name: "ociImage",
                        label: "",
                        required: true,
                        readonly: false,
                        placeholder: contactDetailFormik?.values?.isPhysicalOCI
                          ? "Upload Later"
                          : "Upload OCI Card",
                        placeholderUpload: "OCI card uploaded",
                        handleOnClick: handleShowUploadOCIImage,
                        physicalCopyValue:
                          contactDetailFormik?.values?.isPhysicalOCI,
                      }}
                      twoImages={true}
                      formik={contactDetailFormik}
                    />
                  </Grid>
                </Grid>
              )}
            </>
          )}
          {residentStatus === RESIDENT_STATUS?.Oci &&
            !contactDetailFormik.values.isPassportCurrentAddress ? (
            <>
              <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                <TextFieldInput
                  field={{
                    name: "OCIAlternateDocType",
                    label: "Other Document",
                    required: true,
                    readonly: false,
                    maxLength: 50,
                    placeholder: "Other Document Type ",
                  }}
                  formik={contactDetailFormik}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={6}
                xl={6}
                sx={{ display: { xs: "none", sm: "block" } }}
              ></Grid>
              <Grid item xs={12} sm={6} className="widthFullUploadInput">
                <UploadInput
                  field={{
                    name: "OCIAlternateDocImage",
                    label: "",
                    required: false,
                    readonly: false,
                    placeholder: contactDetailFormik?.values?.isPhysicalOtherDoc
                      ? "Upload Later"
                      : "Upload Other Document ",
                    placeholderUpload: "Document uploaded",
                    handleOnClick: handleShowUploadOCIAlternateImage,
                    physicalCopyValue:
                      contactDetailFormik?.values?.isPhysicalOtherDoc,
                  }}
                  formik={contactDetailFormik}
                />
              </Grid>
            </>
          ) : null}

          {applicantNo === 1 ? (
            <>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    color: "#1A407D",
                    fontSize: "14px",
                    fontWeight: "600",
                    lineHeight: "22px",
                    mt: 1,
                  }}
                >
                  Communication Address
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                <GoogleMapsAutocomplete
                  name="communicationAddress.areaName"
                  formik={contactDetailFormik}
                  label="Search For Property, Area, Street Name"
                  required={true}
                  placeholder="Enter Location"
                  variant="outlined"
                  TextFieldProps={{ size: "small" }}
                  onSelect={(data) => handlePlaceChanged(data)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                <TextFieldInput
                  field={{
                    name: "communicationAddress.houseNumber",
                    label: "House Number/Floor/Tower",
                    required: true,
                    readonly: false,
                    placeholder: "Enter House Number/Floor/Tower ",
                    value:
                      contactDetailFormik?.values?.communicationAddress
                        ?.houseNumber,
                  }}
                  formik={contactDetailFormik}
                  error={
                    contactDetailFormik.touched.communicationAddress
                      ?.houseNumber &&
                    Boolean(
                      contactDetailFormik.errors.communicationAddress
                        ?.houseNumber
                    )
                  }
                  helperText={
                    contactDetailFormik.touched.communicationAddress
                      ?.houseNumber &&
                    contactDetailFormik.errors.communicationAddress
                      ?.houseNumber
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                <TextFieldInput
                  field={{
                    name: "communicationAddress.city",
                    label: "City",
                    required: true,
                    readonly: false,
                    placeholder: "City",
                    maxLength: 50,
                    value:
                      contactDetailFormik?.values?.communicationAddress?.city,
                  }}
                  formik={contactDetailFormik}
                  error={
                    contactDetailFormik.touched.communicationAddress?.city &&
                    Boolean(
                      contactDetailFormik.errors.communicationAddress?.city
                    )
                  }
                  helperText={
                    contactDetailFormik.touched.communicationAddress?.city &&
                    contactDetailFormik.errors.communicationAddress?.city
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                <TextFieldInput
                  field={{
                    name: "communicationAddress.state",
                    label: "State",
                    required:
                      contactDetailFormik?.values?.communicationAddress?.country === "" ||
                      contactDetailFormik?.values?.communicationAddress?.country === "India",
                    readonly: false,
                    placeholder: "State",
                    maxLength: 50,
                    value:
                      contactDetailFormik?.values?.communicationAddress
                        ?.state,
                  }}
                  formik={contactDetailFormik}
                  error={
                    contactDetailFormik.touched.communicationAddress?.state &&
                    Boolean(
                      contactDetailFormik.errors.communicationAddress?.state
                    )
                  }
                  helperText={
                    contactDetailFormik.touched.communicationAddress?.state &&
                    contactDetailFormik.errors.communicationAddress?.state
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                <TextFieldInput
                  field={{
                    name: "communicationAddress.country",
                    label: "Country",
                    required: true,
                    readonly: false,
                    placeholder: "Country",
                    maxLength: 50,
                    value:
                      contactDetailFormik?.values?.communicationAddress
                        ?.country,
                  }}
                  formik={contactDetailFormik}
                  error={
                    contactDetailFormik.touched.communicationAddress
                      ?.country &&
                    Boolean(
                      contactDetailFormik.errors.communicationAddress?.country
                    )
                  }
                  helperText={
                    contactDetailFormik.touched.communicationAddress
                      ?.country &&
                    contactDetailFormik.errors.communicationAddress?.country
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                <TextFieldInput
                  field={{
                    name: "communicationAddress.pinCode",
                    label: "Pincode",
                    required:
                      contactDetailFormik?.values?.communicationAddress
                        ?.country === "" ||
                      contactDetailFormik?.values?.communicationAddress
                        ?.country === "India",
                    readonly: false,
                    placeholder: "Pincode",
                    maxLength: 6,
                    value:
                      contactDetailFormik?.values?.communicationAddress
                        ?.pinCode,
                  }}
                  error={
                    contactDetailFormik.touched.communicationAddress
                      ?.pinCode &&
                    Boolean(
                      contactDetailFormik.errors.communicationAddress?.pinCode
                    )
                  }
                  helperText={
                    contactDetailFormik.touched.communicationAddress
                      ?.pinCode &&
                    contactDetailFormik.errors.communicationAddress?.pinCode
                  }
                  formik={contactDetailFormik}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  sx={{
                    color: "#1C252E",
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "22px",
                  }}
                >
                  Permanent Address <span className="asteriskColor"> *</span>
                </Typography>
                <Typography
                  sx={{
                    color: "#1C252E",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "22px",
                    mt: "0px",
                  }}
                >
                  Is the Permanent Address same as the Communication Address?
                </Typography>

                <BoxCheckInput
                  field={{
                    name: "isSameAddress",
                    label: "",
                    required: true,
                    readonly: false,
                    options: STATUS,
                    grid: true,
                    xsCol: 6,
                    smCol: 6,
                    mdCol: 3,
                    lgCol: 3,
                    handleOnChange,
                  }}
                  formik={contactDetailFormik}
                />
              </Grid>

              {contactDetailFormik?.values?.isSameAddress === 0 && (
                <>
                  <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                    <GoogleMapsAutocomplete
                      name="permanentAddress.areaName"
                      formik={contactDetailFormik}
                      label="Search For Property, Area, Street Name"
                      required={true}
                      placeholder="Enter Location"
                      variant="outlined"
                      TextFieldProps={{ size: "small" }}
                      onSelect={(data) => handlePlaceChangedPermanent(data)}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                    <TextFieldInput
                      field={{
                        name: "permanentAddress.houseNumber",
                        label: "House Number/Floor/Tower",
                        required: true,
                        readonly: false,
                        placeholder: "Enter House Number/Floor/Tower ",
                        value:
                          contactDetailFormik?.values?.permanentAddress
                            ?.houseNumber,
                      }}
                      formik={contactDetailFormik}
                      error={
                        contactDetailFormik.touched.permanentAddress
                          ?.houseNumber &&
                        Boolean(
                          contactDetailFormik.errors.permanentAddress
                            ?.houseNumber
                        )
                      }
                      helperText={
                        contactDetailFormik.touched.permanentAddress
                          ?.houseNumber &&
                        contactDetailFormik.errors.permanentAddress
                          ?.houseNumber
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                    <TextFieldInput
                      field={{
                        name: "permanentAddress.city",
                        label: "City",
                        required: true,
                        readonly: false,
                        placeholder: "City",
                        maxLength: 50,
                        value:
                          contactDetailFormik?.values?.permanentAddress?.city,
                      }}
                      formik={contactDetailFormik}
                      error={
                        contactDetailFormik.touched.permanentAddress?.city &&
                        Boolean(
                          contactDetailFormik.errors.permanentAddress?.city
                        )
                      }
                      helperText={
                        contactDetailFormik.touched.permanentAddress?.city &&
                        contactDetailFormik.errors.permanentAddress?.city
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                    <TextFieldInput
                      field={{
                        name: "permanentAddress.state",
                        label: "State",
                        required:
                          contactDetailFormik?.values?.permanentAddress
                            ?.country === "" ||
                          contactDetailFormik?.values?.permanentAddress
                            ?.country === "India",
                        readonly: false,
                        placeholder: "State",
                        maxLength: 50,
                        value:
                          contactDetailFormik?.values?.permanentAddress
                            ?.state,
                      }}
                      formik={contactDetailFormik}
                      error={
                        contactDetailFormik.touched.permanentAddress?.state &&
                        Boolean(
                          contactDetailFormik.errors.permanentAddress?.state
                        )
                      }
                      helperText={
                        contactDetailFormik.touched.permanentAddress?.state &&
                        contactDetailFormik.errors.permanentAddress?.state
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                    <TextFieldInput
                      field={{
                        name: "permanentAddress.country",
                        label: "Country",
                        required: true,
                        readonly: false,
                        placeholder: "Country",
                        maxLength: 50,
                        value:
                          contactDetailFormik?.values?.permanentAddress
                            ?.country,
                      }}
                      formik={contactDetailFormik}
                      error={
                        contactDetailFormik.touched.permanentAddress
                          ?.country &&
                        Boolean(
                          contactDetailFormik.errors.permanentAddress?.country
                        )
                      }
                      helperText={
                        contactDetailFormik.touched.permanentAddress
                          ?.country &&
                        contactDetailFormik.errors.permanentAddress?.country
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                    <TextFieldInput
                      field={{
                        name: "permanentAddress.pinCode",
                        label: "Pincode",
                        required:
                          contactDetailFormik?.values?.permanentAddress
                            ?.country === "" ||
                          contactDetailFormik?.values?.permanentAddress
                            ?.country === "India",
                        readonly: false,
                        placeholder: "Pincode",
                        maxLength: 6,
                        value:
                          contactDetailFormik?.values?.permanentAddress
                            ?.pinCode,
                      }}
                      formik={contactDetailFormik}
                    />
                  </Grid>
                </>
              )}
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    color: "#1C252E",
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "22px",
                  }}
                >
                  Communication Address{" "}
                  <span className="asteriskColor"> *</span>
                </Typography>
                <Typography
                  sx={{
                    color: "#1C252E",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "22px",
                    mt: "0px",
                    mb: "5px",
                  }}
                >
                  Same as Primary Applicant
                </Typography>

                <BoxCheckInput
                  field={{
                    name: "isSameAddress",
                    label: "",
                    required: true,
                    readonly: false,
                    options: STATUS,
                    grid: true,
                    xsCol: 6,
                    smCol: 6,
                    mdCol: 3,
                    lgCol: 3,
                    handleOnChange,
                  }}
                  formik={contactDetailFormik}
                />
              </Grid>
              {contactDetailFormik?.values?.isSameAddress === 0 ? (
                <>
                  {contactDetailFormik?.values?.isSameAddress === 0 && (
                    <>
                      <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                        <GoogleMapsAutocomplete
                          name="permanentAddress.areaName"
                          formik={contactDetailFormik}
                          label="Search For Property, Area, Street Name"
                          required={true}
                          placeholder="Enter Location"
                          variant="outlined"
                          TextFieldProps={{ size: "small" }}
                          onSelect={(data) => handlePlaceChangedPermanent(data)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                        <TextFieldInput
                          field={{
                            name: "permanentAddress.houseNumber",
                            label: "House Number/Floor/Tower",
                            required: true,
                            readonly: false,
                            placeholder: "Enter House Number/Floor/Tower ",
                            maxLength: 20,
                            value:
                              contactDetailFormik?.values?.permanentAddress
                                ?.houseNumber,
                          }}
                          formik={contactDetailFormik}
                          error={
                            contactDetailFormik.touched.permanentAddress
                              ?.houseNumber &&
                            Boolean(
                              contactDetailFormik.errors.permanentAddress
                                ?.houseNumber
                            )
                          }
                          helperText={
                            contactDetailFormik.touched.permanentAddress
                              ?.houseNumber &&
                            contactDetailFormik.errors.permanentAddress
                              ?.houseNumber
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                        <TextFieldInput
                          field={{
                            name: "permanentAddress.country",
                            label: "Country",
                            required: true,
                            readonly: false,
                            placeholder: "Country",
                            maxLength: 50,
                            value:
                              contactDetailFormik?.values?.permanentAddress
                                ?.country,
                          }}
                          formik={contactDetailFormik}
                          error={
                            contactDetailFormik.touched.permanentAddress
                              ?.country &&
                            Boolean(
                              contactDetailFormik.errors.permanentAddress
                                ?.country
                            )
                          }
                          helperText={
                            contactDetailFormik.touched.permanentAddress
                              ?.country &&
                            contactDetailFormik.errors.permanentAddress?.country
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                        <TextFieldInput
                          field={{
                            name: "permanentAddress.state",
                            label: "State",
                            required:
                              contactDetailFormik?.values?.permanentAddress
                                ?.country === "" ||
                              contactDetailFormik?.values?.permanentAddress
                                ?.country === "India",
                            readonly: false,
                            placeholder: "State",
                            maxLength: 50,
                            value:
                              contactDetailFormik?.values?.permanentAddress
                                ?.state,
                          }}
                          formik={contactDetailFormik}
                          error={
                            contactDetailFormik.touched.permanentAddress
                              ?.state &&
                            Boolean(
                              contactDetailFormik.errors.permanentAddress?.state
                            )
                          }
                          helperText={
                            contactDetailFormik.touched.permanentAddress
                              ?.state &&
                            contactDetailFormik.errors.permanentAddress?.state
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                        <TextFieldInput
                          field={{
                            name: "permanentAddress.city",
                            label: "City",
                            required: true,
                            readonly: false,
                            placeholder: "City",
                            maxLength: 50,
                            value:
                              contactDetailFormik?.values?.permanentAddress
                                ?.city,
                          }}
                          formik={contactDetailFormik}
                          error={
                            contactDetailFormik.touched.permanentAddress
                              ?.city &&
                            Boolean(
                              contactDetailFormik.errors.permanentAddress?.city
                            )
                          }
                          helperText={
                            contactDetailFormik.touched.permanentAddress
                              ?.city &&
                            contactDetailFormik.errors.permanentAddress?.city
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
                        <TextFieldInput
                          field={{
                            name: "permanentAddress.pinCode",
                            label: "Pincode",
                            required:
                              contactDetailFormik?.values?.permanentAddress
                                ?.country === "" ||
                              contactDetailFormik?.values?.permanentAddress
                                ?.country === "India",
                            readonly: false,
                            placeholder: "Pincode",
                            maxLength: 6,
                            value:
                              contactDetailFormik?.values?.permanentAddress
                                ?.pinCode,
                          }}
                          formik={contactDetailFormik}
                        />
                      </Grid>
                    </>
                  )}
                </>
              ) : null}
            </>
          )}
        </Grid>

        {showUploadAadharImage && (
          <UploadPopupTwoImages
            heading="Upload your Aadhaar Card"
            totalImages={2}
            closePopup={handleShowUploadAadharImage}
            handleImageValue={handleImageValue}
            uploadedImageFront={
              contactDetailFormik?.values?.aadhaarImage[0] || []
            }
            uploadedImageBack={
              contactDetailFormik?.values?.aadhaarImage[1] || []
            }
            fieldName={"aadhaarImage"}
            handleUpdateImageData={handleUpdateImageData}
            handlePhysicalImage={handlePhysicalImage}
            selectedOptionValue={contactDetailFormik?.values?.isPhysicalAadhaar}
          />
        )}

        {showUploadPanImage && (
          <UploadPopup
            heading="Upload Your PAN Card"
            totalImages={1}
            closePopup={handleShowUploadPanImage}
            handleImageValue={handleImageValue}
            uploadedImage={contactDetailFormik?.values?.panImage}
            fieldName={"panImage"}
            handleUpdateImageData={handleUpdateImageData}
            handlePhysicalImage={handlePhysicalImage}
            selectedOptionValue={contactDetailFormik?.values?.isPhysicalPan}
          />
        )}
        {showUploadPassportImage && (
          <UploadPopupTwoImages
            heading="Upload Your Passport Photo"
            totalImages={2}
            closePopup={handleShowUploadPassportImage}
            handleImageValue={handleImageValue}
            uploadedImageFront={
              contactDetailFormik?.values?.passportImage[0] || []
            }
            uploadedImageBack={
              contactDetailFormik?.values?.passportImage[1] || []
            }
            fieldName={"passportImage"}
            handleUpdateImageData={handleUpdateImageData}
            handlePhysicalImage={handlePhysicalImage}
            selectedOptionValue={
              contactDetailFormik?.values?.isPhysicalPassport
            }
          />
        )}
        {showUploadOCIImage && (
          <UploadPopupTwoImages
            heading="Upload Your OCI Card"
            totalImages={2}
            closePopup={handleShowUploadOCIImage}
            handleImageValue={handleImageValue}
            uploadedImageFront={contactDetailFormik?.values?.ociImage[0] || []}
            uploadedImageBack={contactDetailFormik?.values?.ociImage[1] || []}
            fieldName={"ociImage"}
            handleUpdateImageData={handleUpdateImageData}
            handlePhysicalImage={handlePhysicalImage}
            selectedOptionValue={contactDetailFormik?.values?.isPhysicalOCI}
          />
        )}
        {showUploadOCIAlternateImage && (
          <UploadPopup
            heading="Upload Your OCI Alternate Photo"
            totalImages={1}
            closePopup={handleShowUploadOCIAlternateImage}
            handleImageValue={handleImageValue}
            uploadedImage={contactDetailFormik?.values?.OCIAlternateDocImage}
            fieldName={"OCIAlternateDocImage"}
            handleUpdateImageData={handleUpdateImageData}
            handlePhysicalImage={handlePhysicalImage}
            selectedOptionValue={
              contactDetailFormik?.values?.isPhysicalOtherDoc
            }
          />
        )}
        <CommonButtonVoucherForm
          handlePrevious={handlePrevious}
          handleNext={handleSaveForm}
          innerSteps={true}
          saveAndPreview={saveAndPreview}
          applicantNo={applicantNo}
          saveDraftData={{
            ...createContactDetailsPayload(true),
            lastStep: Math.max(applicantData?.lastStep, activeStep),

          }}
        />
      </form>
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </div>
  );
});



ContactDetails.propTypes = {
  applicantNo: PropTypes.any,
};

export default ContactDetails;